/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  inject,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Router, RouterLink } from '@angular/router';
import { CartItemListComponent } from '@spartacus/cart/base/components';
import {
  CartItemComponentOptions,
  CartOutlets,
  OrderEntry,
  PromotionLocation,
  SelectiveCartFacade,
} from '@spartacus/cart/base/root';
import { BaseSiteService, UserIdService, TranslatePipe, UrlPipe } from '@spartacus/core';
import { ICON_TYPE, OutletContextData, SpinnerComponent, MediaComponent, AtMessageDirective } from '@spartacus/storefront';
import { UserProfileFacade } from '@spartacus/user/profile/root';
import { BehaviorSubject, Observable, Subject, Subscription, of } from 'rxjs';
import { filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { UdxActiveCartFacade } from 'src/app/udxb2b/facade/udx-active-cart.facade';
import { UdxMultiCartFacade } from 'src/app/udxb2b/facade/udx-multi-cart.facade';
import { Udxb2bCalendarFacade } from 'src/app/udxb2b/facade/udxb2b-calendar-facade';
import { USJB2BPortalConfig, storeFrontConfig, userRoleValue } from 'src/app/udxb2b/shared/constant/storefront-config';
import { OrderModificationPayload, Udxb2bAttractionEvent, Udxb2bDateAndTimeAvailabityResponse, Udxb2bInventoryEvents } from '../../model';
import { BundleProductPriceDetails, PackageEntryInfo, TicketEntryInfo } from '../../model/augmented-core.model';
import { UDXAppConstants } from 'src/app/udxb2b/utilities/constants/udx-app.constants';
import { UDXAppUtils } from 'src/app/udxb2b/utilities/constants/udx-app.utils';
import { NgStyle, NgClass, AsyncPipe, CurrencyPipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatOptionModule } from '@angular/material/core';
import { Udxb2bItemCounterComponent } from '../../item-counter/item-counter.component';
import { DayjsFormatPipe } from '../../../utilities/date/dayjs-format.pipe';

interface ItemListContext {
  readonly?: boolean;
  hasHeader?: boolean;
  options?: CartItemComponentOptions;
  cartId?: string;
  items?: OrderEntry[];
  promotionLocation?: PromotionLocation;
  cartIsLoading?: boolean;
}

@Component({
    selector: 'cx-cart-item-list',
    templateUrl: './cart-item-list.component.html',
    styleUrls: ['./cart-item-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerComponent, ReactiveFormsModule, RouterLink, MediaComponent, NgStyle, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatProgressSpinnerModule, MatSelectModule, MatOptionModule, NgClass, Udxb2bItemCounterComponent, AtMessageDirective, AsyncPipe, CurrencyPipe, TranslatePipe, UrlPipe, DayjsFormatPipe]
})
export class UdxCartItemListComponent extends CartItemListComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject<void>();
  readonly appConstants = UDXAppConstants;
  protected override userId!: string;
  activateRoute = inject(Router);
  displaycartItemLabel = false;
  @Input() override readonly: boolean = false;

  @Input() override hasHeader: boolean = true;

  @Input() override options!: CartItemComponentOptions;

  iconTypes = ICON_TYPE;
  override readonly CartOutlets = CartOutlets;

  @Input() override cartId!: string;

  protected override _items: OrderEntry[] = [];
  override form: UntypedFormGroup = new UntypedFormGroup({});

  disablePickerBtn = true;
  apiLoading$ = new BehaviorSubject(false);

  protected superUser: boolean = false;
  defaultCurrencySymbolForBaseStore: any;
  defaultCurrencyISO: any;

  @Input('items')
  override set items(items: OrderEntry[]) {
    this.resolveItems(items);
    this.createForm();
  }
  override get items(): OrderEntry[] {
    return this._items;
  }

  @Input() override promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;

  @Input('cartIsLoading') override set setLoading(value: boolean) {
    if (!this.readonly) {
      // Whenever the cart is loading, we disable the complete form
      // to avoid any user interaction with the cart.
      value ? this.form.enable({ emitEvent: false }) : this.form.disable({ emitEvent: false });
      this.cd.markForCheck();
    }
  }

  @Input() displayTicketID: boolean = false;

  @Input() displayProductUnitPrice: boolean = false;

  @Input() displayProductVariantTotalPrice: boolean = false;

  @Input() displayTotalPriceColumn: boolean = true;

  dateFilter = (date: any): boolean => {
    return false;
  };

  productUnitPrice?: string;

  currentBaseSite: string = '';

  currentUserRole: string = '';
  UDXshowFields = false;
  usjCode = false;
  dateLabelDisplay$ = new BehaviorSubject(false)
  dateTableColDisplay$ = new BehaviorSubject(true)

  constructor(
    override activeCartService: UdxActiveCartFacade,
    private udxb2bCalendarFacade: Udxb2bCalendarFacade,
    selectiveCartService: SelectiveCartFacade,
    userIdService: UserIdService,
    override multiCartService: UdxMultiCartFacade,
    cd: ChangeDetectorRef,
    protected userProfileFacade: UserProfileFacade,
    protected _baseSiteService: BaseSiteService,
    @Optional() outlet?: OutletContextData<ItemListContext>
  ) {
    super(activeCartService, selectiveCartService, userIdService, multiCartService, cd, outlet);
  }

  getImageForProduct(item: OrderEntry) {
    const prod = item.baseProduct ? item.baseProduct : item.product;
    if (prod?.images && prod?.images['PRIMARY']) {
      return prod?.images['PRIMARY'];
    }
    return prod?.images;
  }

  override ngOnInit(): void {
    // this.readonly = this.options?.readonly!;

    //Calling the function to get the basesite - Mehul
    this.getCurrentStoreBaseSite();

    this.currentUserRole = userRoleValue.filter((entry) => entry.baseSiteId === this.currentBaseSite)[0].userRole!;

    this.subscription.add(this.getInputsFromContext());

    this.subscription.add(this.userIdService?.getUserId().subscribe((userId) => (this.userId = userId)));

    this.subscription.add(
      this.userProfileFacade
        .get()
        .pipe( take(1),
          map(
            (res) => (this.superUser = res ? (res.roles ? res?.roles?.includes(this.currentUserRole) : false) : false)
          )
        )
        .subscribe()
    );
    let currentRoute = this.activateRoute.url;
    if (currentRoute?.includes('my-account/order')) {
      this.displaycartItemLabel = true;
    }
  }

  getDefaultPerfomance(timeSlots: Set<string> | undefined) {
    if (timeSlots && timeSlots.size === 1) {
      const [first] = timeSlots;
      return first;
    }
    return null;
  }

  protected override getInputsFromContext(): Subscription | undefined {
    return this.outlet?.context$.pipe(takeUntil(this.destroy$)).subscribe((context) => {
      if (context.readonly !== undefined) {
        this.readonly = context.readonly;
      }
      if (context.hasHeader !== undefined) {
        this.hasHeader = context.hasHeader;
      }
      if (context.options !== undefined) {
        this.options = context.options;
      }
      if (context.cartId !== undefined) {
        this.cartId = context.cartId;
      }
      if (context.items !== undefined) {
        this.items = context.items;
      }
      if (context.promotionLocation !== undefined) {
        this.promotionLocation = context.promotionLocation;
      }
      if (context.cartIsLoading !== undefined) {
        this.setLoading = context.cartIsLoading;
      }
    });
  }

  convertQty(qty: string) {
    return Number(qty);
  }

  onQuantityChanged(quantityChanged: number, groupedOrderEntry: OrderEntry, currentOrderEntry: OrderEntry): void {
    groupedOrderEntry.dirty = true;
    this.updateEnabledForOrderEntry(groupedOrderEntry);
    const formControlName = currentOrderEntry.product?.code + groupedOrderEntry.variantGroupingCode!;
    this.form.get(formControlName)?.setValue(quantityChanged);
    groupedOrderEntry.timeSlots = new Set();
    const code = groupedOrderEntry.baseProduct
      ? groupedOrderEntry.variantGroupingCode
      : groupedOrderEntry.product?.code;
    this.form.get(code + '-date')?.reset();
    this.cd.markForCheck;
  }

  onQuantityChangedNonVariant(quantityChanged: number, entry: OrderEntry) {
    if (entry.product?.bundlePrice != undefined) {
      entry.combinedVariantPrice = of(entry.product?.bundlePrice?.totalPrice! * quantityChanged);
    } else {
      entry.combinedVariantPrice = of(entry.basePrice?.value! * quantityChanged);
    }
    // Raj: To enable update button
    entry.dirty = true;
    this.updateEnabledForOrderEntry(entry);
  }

  onPickerToggle(entry: OrderEntry, orderModificationPayload?: OrderModificationPayload, referCanBeVisited = false) {
    if (!this.readonly) {
      entry.isLoadingDates = true;
      this.cd.markForCheck;
      this.udxb2bCalendarFacade.fetchAvailableDatesForCart(this.form, entry, orderModificationPayload).subscribe(
        (response) => {
          entry.availableTimeSlotAndPrice = new Map();
          this.caluclateAvailableDateAndTimeSlot(entry, response, referCanBeVisited);
        },
        (error) => {
          console.error('Error loading available dates', error);
          entry.isLoadingDates = false;
        }
      );
    }
  }

  timeSlotSelected(event: MatSelectChange, groupedOrderEntry: OrderEntry) {
    if (event.value) {
      // update cart
      groupedOrderEntry.dirty = true;
      if (! groupedOrderEntry?.updatedSelectedDate) {
        groupedOrderEntry.updatedSelectedDate = new Date(groupedOrderEntry?.selectedDateToVisit!);
      }
      groupedOrderEntry.updatedSelectedTimeslot = event.value;
      this.updateEnabledForOrderEntry(groupedOrderEntry);
    }
  }

  dateSelected(date: MatDatepickerInputEvent<Date>, groupedOrderEntry: OrderEntry) {
    if (date.value) {
      groupedOrderEntry.dirty = true;
      groupedOrderEntry.updatedSelectedDate = new Date(date.value);
      groupedOrderEntry.timeSlots = new Set();
      const code = groupedOrderEntry.baseProduct
        ? groupedOrderEntry.variantGroupingCode
        : groupedOrderEntry.product?.code;
      this.form.get(code + '-date')?.setValue(date.value, { emitEvent: false });
      const selectedDate = this.dateToString(new Date(date.value)).trim();
      this.setSubTotal(selectedDate, groupedOrderEntry);
      this.updateEnabledForOrderEntry(groupedOrderEntry);
      this.cd.detectChanges;
    }
  }

  setSubTotal(selectedDate: string, entry: OrderEntry) {
    let total = 0;
    if (entry.variants) {
      entry.variantOptions?.forEach((options) => {
        const formControlName = options.product?.code! + entry.variantGroupingCode;
        if (this.form.get(formControlName)?.value > 0) {
          const pricing = this.getPricing(entry, options, selectedDate);
          total = total + pricing[0]?.amount * this.form.get(formControlName)?.value;
        }
      });
    } else {
      const pricing = this.getPricing(entry, entry, selectedDate);
      let formControlName = entry.variantGroupingCode
        ? entry.product?.code! + entry.variantGroupingCode
        : entry.product?.code!;
      total = total + pricing?.[0]?.amount * this.form.get(formControlName)?.value;
    }
    // if (entry.product?.bundlePrice) {

    // }
    entry.combinedVariantPrice = of(total);
    this.cd.markForCheck;
  }

  private getPricing(groupedOrderEntry: OrderEntry, options: OrderEntry, selectedDate: string) {
    const calendarDates = groupedOrderEntry.availableTimeSlotAndPrice?.get(options.product?.code!);
    if (calendarDates) {
      const pricing = calendarDates
        .filter((cal: { date: string }) => cal.date === selectedDate)
        .flatMap((obj: { pricing: [] }) => obj.pricing);
      this.setTimeSlotsForEvents(calendarDates, selectedDate, groupedOrderEntry);
      return pricing;
    }
  }

  setTimeSlotsForEvents(calendarDates: any, selectedDate: string, groupedOrderEntry: OrderEntry) {
    if (groupedOrderEntry.selectedTimeSlot) {
      const events: Udxb2bInventoryEvents[] = calendarDates
        .filter((cal: { date: string }) => cal.date === selectedDate)
        .flatMap((obj: { inventoryEvents: Udxb2bInventoryEvents[] }) => obj.inventoryEvents);

      events.forEach((time: Udxb2bInventoryEvents) => {
        this.extractTimefromStartDate(time, groupedOrderEntry);
      });
    }
  }

  private extractTimefromStartDate(time: Udxb2bInventoryEvents, groupedOrderEntry: OrderEntry) {
    var timeAndDate = time.startDate!.split(/(\s+)/);
    if (timeAndDate[2] && timeAndDate[4] && groupedOrderEntry.timeSlots) {
      groupedOrderEntry.timeSlots.add(timeAndDate[2] + ' ' + timeAndDate[4]);
    }
  }

  private caluclateAvailableDateAndTimeSlot(
    groupedOrderEntry: OrderEntry,
    response: Udxb2bDateAndTimeAvailabityResponse,
    referCanBeVisited = false
  ) {
    const dates = response.eventAvailability?.flatMap((calAvail) => calAvail.calendarDates?.map((date) => date.date));
    const availableDates = dates?.filter((date) => {
      return response.eventAvailability?.every((avail) => {
        return avail.calendarDates?.some((calDate) => {
          var shouldInclude: boolean | undefined = false;
          if (calDate.date === date) {
            if (referCanBeVisited) {
              shouldInclude = calDate.canBeVisited;
            } else {
              shouldInclude =
                calDate.date === date &&
                calDate.pricing &&
                calDate.pricing?.length > 0;
            }
            if (shouldInclude && this.usjCode) {
              // If above tests are passed next check for -ve difference value. Check comment in USJT-15201 (https://nbcu-ot.atlassian.net/browse/USJT-15201?focusedCommentId=4751305)
              if (calDate.pricing && calDate.pricing?.length && calDate.pricing[0].difference !== undefined) {
                if (calDate.pricing[0].difference < 0) {
                  shouldInclude = false;
                }
              }
            }
          }
          return shouldInclude;
        });
      });
    });
    response.eventAvailability?.forEach((event) => {
      if (groupedOrderEntry.availableTimeSlotAndPrice && event.partNumber && event.calendarDates) {
        groupedOrderEntry.availableTimeSlotAndPrice.set(event.partNumber, event.calendarDates);
      }
    });
    this.dateFilter = (date: any): boolean => {
      const dateSearch = this.dateToString(new Date(date)).trim();
      const uniqueElements = [...new Set(availableDates)];
      return uniqueElements.includes(dateSearch);
    };
    groupedOrderEntry.isLoadingDates = false;
    this.cd.detectChanges();
  }

  dateToString(date: Date): string {
    return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
  }

  formatVisitationDate(date: Date): string {
    return ('0' + (date.getMonth() + 1)).slice(-2) + '/' + ('0' + date.getDate()).slice(-2) + '/' + date.getFullYear();
  }

  /**
   * Resolves items passed to component input and updates 'items' field
   */
  protected override resolveItems(items: OrderEntry[]): void {
    if (!items) {
      this._items = [];
      return;
    }
    this._items = [];
    const groupedEntries = items.reduce((acc: { [key: string]: OrderEntry[] }, entry: OrderEntry) => {
      if (entry.baseProduct) {
        const groupingCode = entry.variantGroupingCode;
        if (!acc[groupingCode as string]) {
          acc[groupingCode as string] = [entry];
        } else {
          acc[groupingCode as string].push(entry);
        }
      } else {
        // If there is no base Product . It means it should be package or coupon/voucher
        const packageOrCouponEntry = Object.assign({ selected: false }, entry);
        this.setDateAndTimeVisit(
          packageOrCouponEntry,
          packageOrCouponEntry.ticketEntryInfo!,
          packageOrCouponEntry.packageEntryInfo!
        );
        this.setPackageProductPriceUnit(entry, packageOrCouponEntry);
        packageOrCouponEntry.ticketCodesDisplayString = entry.ticketCodes?.join(', ');
        this._items.push(packageOrCouponEntry);
      }
      return acc;
    }, {});

    Object.entries(groupedEntries).forEach(([key, value]) => {
      const orderEntry = value[0];
      if (value.length > 1) {
        let totalPrice: number = 0;
        var variantPriceStrings = '';
        var variantTicketCodes = '';
        value.forEach((variant) => {
          totalPrice = totalPrice + variant.basePrice?.value! * variant.quantity!;
          let unitPrice = variant.basePrice?.formattedValue;
          let variantType = variant.product?.name;
          variantPriceStrings = variantPriceStrings + variantType + ': ' + unitPrice + '\n\n';
          let ticketCodesArray = variant.ticketCodes;
          variantTicketCodes = variantTicketCodes + variantType + ': ' + ticketCodesArray?.join(', ') + '\n\n';
        });
        var groupedOrderEntry: OrderEntry = {
          variants: true,
          baseProduct: orderEntry.baseProduct,
          variantOptions: [...value],
          combinedVariantPrice: of(totalPrice),
          isLoadingDates: false,
          unitPriceDisplayString: variantPriceStrings,
          ticketCodesDisplayString: variantTicketCodes,
          variantGroupingCode: orderEntry.variantGroupingCode,
        };
        this.setDateAndTimeVisit(groupedOrderEntry, value[0].ticketEntryInfo!, value[0].packageEntryInfo!);
        this._items.push(groupedOrderEntry);
      } else {
        const singleEntry = Object.assign({ selected: false }, orderEntry);
        this.setDateAndTimeVisit(singleEntry, orderEntry.ticketEntryInfo!, orderEntry.packageEntryInfo!);
        singleEntry.unitPriceDisplayString = orderEntry.basePrice?.formattedValue;
        singleEntry.ticketCodesDisplayString = orderEntry.ticketCodes?.join(', ');
        this._items.push(singleEntry);
      }
    });
  }

  setPackageProductPriceUnit(originalEntry: OrderEntry, cloneEntry: OrderEntry) {
    if (originalEntry.product?.bundlePrice) {
      const bundlePrice: BundleProductPriceDetails = originalEntry.product?.bundlePrice;
      if (bundlePrice.subtotal) {
        cloneEntry.unitPriceDisplayString = this.defaultCurrencySymbolForBaseStore + String(bundlePrice.subtotal);
      }
    } else {
      cloneEntry.unitPriceDisplayString = originalEntry.basePrice?.formattedValue;
    }
  }

  setDateAndTimeVisit(entry: OrderEntry, ticketEntryData: TicketEntryInfo, packageEntryInfo: PackageEntryInfo) {
    if (ticketEntryData && ticketEntryData.visitationDate) {
      var timeAndDate = ticketEntryData.visitationDate.split(/(\s+)/);
      this.setDateAndTimeSlots(entry, timeAndDate);
    } else if (packageEntryInfo && packageEntryInfo.visitationDate) {
      var timeAndDate = packageEntryInfo.visitationDate.split(/(\s+)/);
      this.setDateAndTimeSlots(entry, timeAndDate);
    }
  }

  private setDateAndTimeSlots(entry: OrderEntry, timeAndDate: string[]) {
    entry.selectedDateToVisit = timeAndDate[0];
    if (timeAndDate[2] && timeAndDate[4]) {
      entry.selectedTimeSlot = timeAndDate[2] + ' ' + timeAndDate[4];
    } else {
      entry.selectedTimeSlot = timeAndDate[2];
    }
    if (entry?.variantGroupingCode) {
      if (this.form.get(entry?.variantGroupingCode + '-date')?.value) {
        entry.selectedDateToVisit = this.form.get(entry?.variantGroupingCode + '-date')?.value;
      }
    }
  }

  /**
   * Creates form models for list items
   */
  protected override createForm(): void {
    const formGroupFields = this.getFormControlsFields();
    this.form = new UntypedFormGroup(formGroupFields);
  }

  getFormControlsFields() {
    const formGroupFields = {} as string | any;
    this._items.forEach((item) => {
      if (item.variants) {
        item.variantOptions?.forEach((variant) => {
          const qty: number = variant?.quantity!;
          formGroupFields[variant?.product?.code! + item.variantGroupingCode] = new UntypedFormControl(qty);
        });
      } else {
        if (item.variantGroupingCode) {
          formGroupFields[item?.product?.code! + item.variantGroupingCode] = new UntypedFormControl(item?.quantity);
        } else {
          formGroupFields[item?.product?.code!] = new UntypedFormControl(item?.quantity);
        }
      }
      this.createDateAndTimeFormControl(formGroupFields, item);
    });
    return formGroupFields;
  }

  createDateAndTimeFormControl(formGroupFields: string | any, item: OrderEntry) {
    let formControlName = item.baseProduct ? item.variantGroupingCode : item.product?.code; // keep base product code as the control name for date and time of visit.
    if (item.selectedDateToVisit) {
      formGroupFields[formControlName + '-date'] = new UntypedFormControl(
        new Date(item.selectedDateToVisit).toISOString()
      );
    }
    if (item.selectedTimeSlot) {
      formGroupFields[formControlName + '-time'] = new UntypedFormControl(item.selectedTimeSlot);
      if (item.ticketEntryInfo) {
        item.timeSlots = item.ticketEntryInfo?.timeSlots;
      } else if (item.packageEntryInfo) {
        item.timeSlots = item.packageEntryInfo?.timeSlots;
      }
    }
  }

  protected override getControlName(item: OrderEntry): string {
    return item.entryNumber?.toString() || '';
  }

  override removeEntry(item: OrderEntry): void {
    if (this.options.isSaveForLater) {
      this.selectiveCartService.removeEntry(item);
    } else if (this.cartId && this.userId) {
      if (item.variants) {
        // item.variantOptions?.forEach((option) => {
        //   this.multiCartService.removeUdxEntry(this.userId, this.cartId, option.entryNumber as number, [{}]);
        // });
        if (item.variantOptions) {
          this.multiCartService.removeUdxEntry(
            this.userId,
            this.cartId,
            undefined,
            item.variantOptions.map((option) => option.entryNumber)
          );
        }
      } else {
        this.multiCartService.removeUdxEntry(this.userId, this.cartId, item.entryNumber as number, undefined);
      }
    } else {
      this.activeCartService.removeUdxEntry(item, [{}]);
    }
    delete this.form.controls[this.getControlName(item)];
  }

  override getControl(item: OrderEntry): Observable<UntypedFormGroup> | undefined {
    return this.form.get(this.getControlName(item))?.valueChanges.pipe(
      // eslint-disable-next-line import/no-deprecated
      startWith(null),
      tap((value) => {
        if (item.updateable && value && !this.readonly) {
          if (this.options.isSaveForLater) {
            this.selectiveCartService.updateEntry(value.entryNumber, value.quantity);
          } else if (this.cartId && this.userId) {
            this.multiCartService.updateUdxEntry(this.userId, this.cartId, value.entryNumber, value.quantity, [{}]);
          } else {
            this.activeCartService.updateUdxEntry(value.entryNumber, value.quantity, [{}]);
          }
        }
      }),
      map(() => <UntypedFormGroup>this.form.get(this.getControlName(item)))
    );
  }

  override ngOnDestroy(): void {
    this.subscription?.unsubscribe();
     this.destroy$.next();
    this.destroy$.complete();
  }

  updateEnabledForOrderEntry(anOrderEntry: OrderEntry): void {
    // anOrderEntry -> here is the base product
    var shouldEnableUpdateButton = false;
    if (anOrderEntry.dirty) {
      shouldEnableUpdateButton = true;
      if (anOrderEntry.selectedDateToVisit && !anOrderEntry.updatedSelectedDate) {
        // If date selection is applicable for this entry then the date should be selected by the user explicitly for update button to be enabled
        shouldEnableUpdateButton = false;
      }
      if (
        anOrderEntry.selectedTimeSlot &&
        !anOrderEntry.updatedSelectedTimeslot &&
        anOrderEntry.timeSlots &&
        anOrderEntry.timeSlots.size > 1
      ) {
        // If timeslot selection is applicable for this entry then the timeslot should be selected by the user explicitly for update button to be enabled
        shouldEnableUpdateButton = false;
      }
    }
    anOrderEntry.shouldEnableUpdate = shouldEnableUpdateButton;
  }

  performUpdateEntry(anOrderEntry: OrderEntry) {
    var orderEntriesArray: any[] = [];
    var selectedDate: any = null;
    if (anOrderEntry.selectedDateToVisit) {
      selectedDate = anOrderEntry.updatedSelectedDate
        ? this.formatVisitationDate(anOrderEntry.updatedSelectedDate!).trim()
        : this.formatVisitationDate(new Date(anOrderEntry.selectedDateToVisit));
    }
    var timeslot: any = null;
    if (anOrderEntry.selectedTimeSlot) {
      timeslot = anOrderEntry.updatedSelectedTimeslot
        ? anOrderEntry.updatedSelectedTimeslot
        : anOrderEntry.selectedTimeSlot;
      selectedDate = (selectedDate + ' ' + timeslot).trim();
    }
    if (anOrderEntry.variants && anOrderEntry.variantOptions) {
      // Products with variants like Studio passes, Annual pass etc where there are age selectors configured as variants.
      anOrderEntry.variantOptions?.forEach((option) => {
        let variantCode = option.product?.code;
        var variantQuantity;
        const formControlName = variantCode! + anOrderEntry.variantGroupingCode!;
        variantQuantity = this.form.get(formControlName)?.value;
        if (!variantQuantity && option.quantity) {
          // If the value is not updated explicitly by user pick the default value!
          variantQuantity = option.quantity;
        }
        var variantEntryObject: any = {};
        variantEntryObject.entryNumber = option.entryNumber;
        variantEntryObject.quantity = variantQuantity;
        variantEntryObject.product = {
          code: variantCode,
        };
        if (selectedDate) {
          variantEntryObject.ticketEntryInfo = {
            visitationDate: selectedDate,
            timeSlots: Array.from(anOrderEntry.timeSlots ? anOrderEntry.timeSlots : []),
          };
        }
        orderEntriesArray.push(variantEntryObject);
      });
    } else {
      var variantEntryObject: any = {};
      let productCode = anOrderEntry.product?.code;
      const formControlName = anOrderEntry.variantGroupingCode
        ? productCode + anOrderEntry.variantGroupingCode
        : productCode;
      let productQuantity = this.form.get(formControlName as string)?.value;
      let productEntryNumber = anOrderEntry.entryNumber;
      variantEntryObject.entryNumber = productEntryNumber;
      variantEntryObject.quantity = productQuantity;
      variantEntryObject.product = {
        code: productCode,
      };
      if (selectedDate) {
        variantEntryObject.ticketEntryInfo = {
          visitationDate: selectedDate,
          timeSlots: Array.from(anOrderEntry.timeSlots ? anOrderEntry.timeSlots : []),
        };
      }
      orderEntriesArray.push(variantEntryObject);
    }
    if (orderEntriesArray.length > 0) {
      this.apiLoading$.next(true);
      orderEntriesArray.forEach((aPayload) => {
        var aPayloadArray = [];
        aPayloadArray.push(aPayload);

        this.multiCartService.updateUdxEntry(
          this.userId,
          this.cartId,
          aPayload.entryNumber,
          aPayload.quantity,
          aPayloadArray
        );
        this.multiCartService
          .isStable(this.cartId)
          //.pipe(take(1),filter((isStable) => isStable))
          .pipe(filter((isStable) => isStable))
          .subscribe((res) => {
            this.apiLoading$.next(false);
           // console.log(res);
          });
      });
    }
  }

  //Function to get the base storefront and the currency - Mehul
  getCurrentStoreBaseSite() {
    this._baseSiteService.getActive().pipe(take(1)).subscribe((currentSite) => {
      this.currentBaseSite = currentSite;
      storeFrontConfig
        .filter((config) => config.baseSiteId === this.currentBaseSite)
        .map((config) => {
          this.UDXshowFields = config.active;
        });
      USJB2BPortalConfig.filter((config) => config.baseSiteId === this.currentBaseSite).map((config) => {
        this.usjCode = config.active;
      });
    });
    this._baseSiteService.get(this.currentBaseSite).pipe(take(1)).subscribe((res) => {
     // console.log(res);
      this.defaultCurrencySymbolForBaseStore = res?.baseStore?.defaultCurrency?.symbol;
      this.defaultCurrencyISO = res?.baseStore?.defaultCurrency?.isocode;
    });
  }
  shouldEnableActions() {
    return this.isNotReadonlyInNature() || this.shouldEnableDelete();
  }
  shouldEnableDelete() {
    return this.options.shouldEnableDeleteEvenInReadOnly;
  }
  isNotReadonlyInNature() {
    return !this.readonly || this.options.isSaveForLater || this.options.displayAddToCart;
  }
  checkDateDisplay(itemEntry: OrderEntry): boolean {

    if (itemEntry?.product){
      return itemEntry?.product?.dateSelectionRequired ? (this.dateLabelDisplay$.next(true), true ): false;
    }
    return true
  }
  sortEventsByStartTimeAndLandEntry(events: Udxb2bAttractionEvent[]): Udxb2bAttractionEvent[] {
    return UDXAppUtils.sortEventsByStartTimeAndLandEntry(events);
  }
  getEventsGroupedByMainGroup(events: Udxb2bAttractionEvent[]): Udxb2bAttractionEvent[][] {
    /*const groupedMap = new Map<string, Udxb2bAttractionEvent[]>();
    for (const event of events) {
      const key = event.maingroup;
      if (key) {
        if (!groupedMap.has(key)) {
          groupedMap.set(key, []);
        }
        groupedMap.get(key)!.push(event);
      }
    }
    return Array.from(groupedMap.values());*/

    var attractionsGroupedByMainGroup: Udxb2bAttractionEvent[][] = [];
    for (const event of events) {
      const key = event.maingroup;
      if (key) {
        var foundKey = false;
        for (let i = 0; i < attractionsGroupedByMainGroup.length; i++) {
          if (attractionsGroupedByMainGroup[i][0].maingroup === key) {
            attractionsGroupedByMainGroup[i].push(event);
            foundKey = true;
          }
        }
        if (!foundKey) {
          var newArray: Udxb2bAttractionEvent[] = [];
          newArray.push(event);
          attractionsGroupedByMainGroup.push(newArray);
        }
      }
    }
    return attractionsGroupedByMainGroup;
  }
  getEventsGroupedBySubGroup(events: Udxb2bAttractionEvent[]): Udxb2bAttractionEvent[][] {
    /*const groupedMap = new Map<string, Udxb2bAttractionEvent[]>();
    for (const event of events) {
      const key = event.subgroup;
      if (key) {
        if (!groupedMap.has(key)) {
          groupedMap.set(key, []);
        }
        groupedMap.get(key)!.push(event);
      }
    }
    return Array.from(groupedMap.values());*/
    var attractionsGroupedBySubGroup: Udxb2bAttractionEvent[][] = [];
    for (const event of events) {
      const key = event.subgroup;
      if (key) {
        var foundKey = false;
        for (let i = 0; i < attractionsGroupedBySubGroup.length; i++) {
          if (attractionsGroupedBySubGroup[i][0].subgroup === key) {
            attractionsGroupedBySubGroup[i].push(event);
            foundKey = true;
          }
        }
        if (!foundKey) {
          var newArray: Udxb2bAttractionEvent[] = [];
          newArray.push(event);
          attractionsGroupedBySubGroup.push(newArray);
        }
      }
    }
    return attractionsGroupedBySubGroup;
  }
  isAttractionGroupChoice(subGroupEvents: Udxb2bAttractionEvent[]): boolean {
    return UDXAppUtils.isAttractionGroupChoice(subGroupEvents);
  }
  getOnlyChoiceText(subGroupEvents: Udxb2bAttractionEvent[]): string {
    return UDXAppUtils.getOnlyChoiceText(subGroupEvents);
  }
}
